<template>
  <Layout>
    <McLoading :isLoading="isCreatingFO" />
    <AlertQuota :quotaStatus="quotaStatus" />

    <div ref="route-optimization-detail-ref" class="route-optimization-detail">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex gap-2">
            <QuotaCounter
              v-if="showResponse?.status?.raw_type === 'SUCCESS'"
              :refreshTrigger="quotaTrigger"
              class="me-2"
            />
            <McButton
              variant="secondary"
              icon="icon-outline_arrow-left"
              :text="$t('global.back')"
              :disabled="disableBackButton"
              :screenBreakpoint="buttonScreenBreakpoint"
              tooltipPosition="bottom"
              @click="$router.push({ name: 'Route Optimization' })"
            />
            <template v-if="!isLoading && showResponse?.status.raw_type === 'SUCCESS'">
              <McButton
                v-if="!hasFOCreated"
                v-b-tooltip.html.hover.bottom="
                  hasNoUserGroup
                    ? {
                        title: $t('routeOptimization.tooltip.noUserGroup.reOptimize'),
                      }
                    : null
                "
                variant="secondary"
                :text="$t('routeOptimization.changeSettings')"
                icon="icon-outline_settings"
                :disabled="isLoading || isCreatingFO || hasNoUserGroup"
                :screenBreakpoint="buttonScreenBreakpoint"
                tooltipPosition="bottom"
                @click="handleReoptimizeSetting"
              />
              <McButton
                v-else
                variant="secondary"
                :text="$t('routeOptimization.action.viewSettings')"
                icon="icon-outline_options-2"
                :disabled="isLoading || isCreatingFO"
                :screenBreakpoint="buttonScreenBreakpoint"
                tooltipPosition="bottom"
                @click="$router.push({ name: 'Route Optimization Detail Setting', params: { id } })"
              />
              <McButton
                v-if="!isAllFOCreated && hasVehicles"
                :text="$t('routeOptimization.action.createAssignment')"
                :disabled="isLoading || isCreatingFO"
                @click="handleCreateFO"
              />
            </template>
          </div>
        </template>
      </PageHeader>

      <div class="route-optimization-detail-container">
        <McAlert
          v-if="isFOCreated"
          withIcon
          variant="info"
          iconVariant="fill"
          :text="$t('routeOptimization.label.assignmentCreated')"
          class="fo-alert"
        >
          <template #suffix>
            <div class="w-100 d-flex flex-row-reverse route-optimization-detail__fo-list">
              <b-button class="route-optimization-detail__fo-list-btn" @click="handleShowFOList">
                {{ $t('routeOptimization.action.viewAssignmentList') }}
              </b-button>
            </div>
          </template>
        </McAlert>

        <ModalFOList :apiClient="{ get: getFO }" />

        <b-collapse id="map-container" v-model="isShowMap">
          <template v-if="!isLoading">
            <RoutesMap
              v-if="showResponse?.status.raw_type === 'SUCCESS'"
              :vehicles="showResponse?.vehicles"
            />
            <LoadingMap v-if="showResponse?.status.raw_type === 'PENDING'" />
          </template>
        </b-collapse>
        <TimelineTableV2
          v-if="!isLoading"
          :routeOptimizationId="id"
          :showResponse="showResponse"
          :settings="settings"
          :isShowMap="isShowMap"
          :selected="vehicleSelected"
          :isCreatingFO="isCreatingFO"
          @update:isShowMap="isShowMap = $event"
          @update:selected="vehicleSelected = $event"
          @quotaStatus="handleQuotaStatus"
          @assignVehicle="handleAssignVehicle"
          @hasBeenAddedToTrip="(roId, data, res) => handleMoveDeliveryToServedVehicle(roId, data, res)"
        />
        </div>
      </div>
    <ConfirmToChange />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapGetters } from 'vuex'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'

import LoadingMap from './contents/LoadingMap'
import RoutesMap from './contents/RoutesMap'
import TimelineTableV2 from './contents/TimelineTableV2'
import ModalFOList from './components/ModalFOList'
import ToastFOList from './components/ToastFOList'
import ConfirmToChange from './components/Re-Optimize/Modal/ConfirmToChange'
import QuotaCounter from './components/QuotaCounter'
import AlertQuota from './components/QuotaCounter/AlertQuota.vue'

import * as RO from '@/services/rest-api/lib/route-optimization'
import * as FO from '@/services/rest-api/lib/fleet-task/fleet-task'

import { errorCodeToastMap } from './lib/constants/error-code-mappings'

import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'

export default {
  page: {
    title: 'Route Optimization',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McAlert,
    LoadingMap,
    RoutesMap,
    TimelineTableV2,
    ModalFOList,
    ConfirmToChange,
    QuotaCounter,
    AlertQuota,
  },
  data() {
    return {
      isLoading: true,
      isShowMap: true,
      isCreatingFO: false,
      isGatewayTimeout: false,

      timeInterval: null,
      intervalCount: 0,
      maxIntervalCount: 6,
      getFO: null,

      showResponse: {},
      vehicleSelected: {},
      settings: {},
      quotaStatus: 'available',
      quotaTrigger: 0,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasTmsUserGroupScope() {
      return this.hasAccessAll(['tms-user-group:read'])
    },
    buttonScreenBreakpoint() {
      return 1400
    },
    title() {
      return `${this.$t('routeOptimization.detailTitle')} #${this.showResponse.number || ''}`
    },
    disableBackButton() {
      return this.isCreatingFO || (this.isLoading && !this.isGatewayTimeout)
    },
    id() {
      return this.$route.params.id
    },
    isFOCreated() {
      let value = false

      if (!this.isLoading && this.showResponse?.status.raw_type === 'SUCCESS') {
        value = this.showResponse.vehicles.some((vehicle) => vehicle.fleet_task_id !== null)
      }

      return value
    },
    hasFOCreated() {
      if (
        !this.showResponse?.status ||
        this.showResponse?.status.raw_type !== 'SUCCESS' ||
        !this.showResponse.vehicles ||
        !Array.isArray(this.showResponse.vehicles)
      )
        return false
      return this.showResponse.vehicles.some((vehicle) => vehicle.fleet_task_id !== null)
    },
    isAllFOCreated() {
      return (
        !this.isLoading &&
        this.showResponse?.status.raw_type === 'SUCCESS' &&
        this.showResponse.vehicles.length > 0 &&
        this.showResponse.vehicles.every((v) => v.fleet_task_id !== null)
      )
    },
    hasVehicles() {
      return this.showResponse.vehicles.length > 0
    },
    hasNoUserGroup() {
      return this.hasTmsUserGroupScope && this.showResponse?.user_group == null
    },
  },
  watch: {
    '$route.params.id': {
      async handler(newId, oldId) {
        if (newId !== oldId) {
          this.isLoading = true
          this.isGatewayTimeout = false
          const isShowDataSuccess = await this.showData()
          if (!isShowDataSuccess) {
            this.isLoading = false
            return
          }
          await this.getSettings()
          this.isLoading = false
        }
      },
      immediate: false,
    },
  },
  async mounted() {
    const isShowDataSuccess = await this.showData()
    if (!isShowDataSuccess) {
      this.isLoading = false
      return
    }
    await this.handleSetInterval()

    await this.getSettings()

    this.isLoading = false
  },
  beforeDestroy() {
    this.clearTimeInterval()
    this.$store.dispatch('routeOptimization/resetVehiclesRoutes')
  },
  methods: {
    initShowedRoutes() {
      if (this.showResponse.status.raw_type !== 'SUCCESS') return

      const showedRoutes = {}
      this.showResponse.vehicles.forEach((vehicle) => {
        showedRoutes[vehicle.id] = true
      })

      this.$store.dispatch('routeOptimization/initShowedRoutes', showedRoutes)
      this.isLoading = false
    },

    async showData(customId) {
      this.isGatewayTimeout = false
      const res = await RO.show(customId || this.id)

      if (res.errors) {
        if (res.status === 504) {
          this.isGatewayTimeout = true
          this.clearTimeInterval()
          return false
        }

        this.$mcToast.error(this.$t('routeOptimization.alert.operationError'))
        return false
      }

      this.showResponse = res.data
      this.$store.dispatch('routeOptimization/setShowResponse', res.data)
      this.quotaTrigger = Date.now()

      if (this.showResponse.vehicles) {
        this.$store.dispatch('routeOptimization/setVehiclesRoutes', this.showResponse.vehicles)
      }

      await this.$nextTick()

      // Set vehicleSelected: semua yang enabled dan belum terpilih jadi selected
      if (this.showResponse.vehicles && Array.isArray(this.showResponse.vehicles)) {
        const newSelected = {}
        this.showResponse.vehicles.forEach((vehicle) => {
          // enabled jika fleet_task_id === null
          if (vehicle.fleet_task_id === null) {
            newSelected[vehicle.id] = true
          } else {
            newSelected[vehicle.id] = false
          }
        })
        this.vehicleSelected = newSelected
      }

      if (this.showResponse.status.raw_type === 'SUCCESS') {
        this.initShowedRoutes()
        this.clearTimeInterval()
      }

      return true
    },

    clearTimeInterval() {
      if (this.timeInterval) {
        clearInterval(this.timeInterval)
        this.timeInterval = null
      }
      this.intervalCount = 0
    },

    fetchDataPeriodically() {
      this.clearTimeInterval()

      this.timeInterval = setInterval(async () => {
        this.intervalCount++

        const res = await RO.show(this.id)

        if (res.errors) {
          this.clearTimeInterval()
          this.$mcToast.error('Terjadi kesalahan saat memuat data.')
          return
        }

        this.showResponse = res.data
        this.$store.dispatch('routeOptimization/setShowResponse', res.data)
        this.quotaTrigger = Date.now()

        if (res.data.vehicles) {
          this.$store.dispatch('routeOptimization/setVehiclesRoutes', res.data.vehicles)
        }

        if (res.data.status.raw_type === 'SUCCESS') {
          this.initShowedRoutes()
          this.clearTimeInterval()
          return
        }

        if (res.data.status.raw_type === 'FAILED') {
          this.$router.push({ name: 'Route Optimization Detail Setting', params: { id: this.id } })
          return
        }

        if (this.intervalCount >= this.maxIntervalCount) {
          this.clearTimeInterval()
          this.$router.push({ name: 'Route Optimization' })
        }
      }, 10000)
    },

    async handleSetInterval() {
      if (this.showResponse?.status?.raw_type !== 'PENDING') return
      this.fetchDataPeriodically()
    },

    async handleCreateFO() {
      this.isCreatingFO = true
      const data = []

      for (const [key, val] of Object.entries(this.vehicleSelected)) {
        if (!val) continue
        data.push(key)
      }

      if (data.length === 0) {
        this.$mcToast.error('Pilih kendaraan terlebih dahulu')
        this.isCreatingFO = false
        return
      }

      const res = await RO.createFO(this.id, {
        route_optimization_vehicle_ids: data,
      })

      if (res?.errors_v2 !== null && res?.errors_v2?.issues) {
        const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
        let errorMessage = this.$t('global.somethingWentWrong')
        if (issue) {
          errorMessage = this.$t(errorCodeToastMap[issue.code])
        }

        this.$mcToast.error(errorMessage)
        this.isCreatingFO = false
        return
      }

      if (res?.errors) {
        this.assignErrors(res.errors)
        this.isCreatingFO = false
        return
      }

      await this.showData()

      this.isCreatingFO = false
      this.$mcToast.success({
        component: ToastFOList,
        listeners: {
          click: () => this.handleShowFOList(res.data),
        },
      })
    },

    async handleShowFOList(foList = []) {
      const fleetTaskIds = []

      if (foList.length > 0) {
        foList.forEach((item) => {
          fleetTaskIds.push(item.id)
        })
      } else {
        this.showResponse.vehicles.forEach((vehicle) => {
          if (vehicle.fleet_task_id === null) return
          fleetTaskIds.push(vehicle.fleet_task_id)
        })
      }

      let filters = `id in (${fleetTaskIds.join(' ')})`
      this.getFO = FO.get(filters, null, null)
      this.$bvModal.show('mc-modal-fo-list')
    },

    async assignErrors(errors) {
      const newShowResponse = cloneDeep(this.showResponse)
      let vehicleErrors = new Map()
      let orderErrors = 0

      for (const [key, val] of Object.entries(errors)) {
        if (key.includes('route_optimization_vehicle_ids')) continue

        const splittedKeys = key.replace('route_optimization_', '').split('.')

        if (!splittedKeys.includes('delivery_order')) {
          vehicleErrors.set(splittedKeys[1], true)
          set(newShowResponse, [...splittedKeys, 'is_error'], true)

          if (val.includes('vehicle is removed from master data')) {
            set(newShowResponse, [...splittedKeys, 'license_plate'], 'Data Telah Dihapus')
          }

          continue
        }

        if (splittedKeys[3] == 1) {
          orderErrors++
        }

        while (splittedKeys.length > 0) {
          if (splittedKeys.length == 2) break

          set(newShowResponse, [...splittedKeys, 'is_error'], true)

          let removeCount = splittedKeys.includes('delivery_order') ? 3 : 2
          splittedKeys.splice(splittedKeys.length - removeCount, removeCount)
        }
      }

      this.showResponse = newShowResponse

      if (this.showResponse.vehicles) {
        this.$store.dispatch('routeOptimization/setVehiclesRoutes', this.showResponse.vehicles)
      }

      await this.$nextTick()

      if (vehicleErrors.size > 0) {
        this.$mcToast.error(
          `${this.$t('routeOptimization.alert.vehicleUnavailable', { data: vehicleErrors.size })}`
        )
      }

      if (orderErrors > 0) {
        this.$mcToast.error(
          `${this.$t('routeOptimization.alert.deliveryStatusInvalid', { data: orderErrors })}`
        )
      }
    },

    handleReoptimizeSetting() {
      if (!this.isLoading && this.showResponse?.status.raw_type === 'SUCCESS') {
        const vehicles = this.showResponse.vehicles
        const hasError = vehicles.some((vehicle) => vehicle.is_error === true)

        if (hasError) {
          this.$bvModal.show('modal-confirm-to-change')
          return
        }

        this.$router.push({
          name: 'Route Reoptimization Setting',
          params: { id: this.$route.params.id },
        })
      }
    },

    async getSettings(customId) {
      const res = await RO.showSetting(customId || this.id)

      this.settings = res.data
      if (res.errors) return
    },

    handleQuotaStatus(status) {
      this.quotaStatus = status
    },
    async handleAssignVehicle(payload) {
      try {
        const response = await RO.assignVehicle(payload.routeOptimizationId, payload.currentVehicleId, payload.data)
        const newId = response?.data?.id
        if (newId) {
          // Update router params - watcher akan menangani showData dan getSettings
          this.$router.replace({ params: { id: newId } })
        }
      } catch (error) {
        console.error('Error assigning vehicle:', error)
      }
    },
    async handleMoveDeliveryToServedVehicle(roId, data, response) {
      try {
        const newId = response?.data?.id
        if (newId) {
          // Update router params - watcher akan menangani showData dan getSettings
          this.$router.replace({ params: { id: newId } })
        }
      } catch (error) {
        console.error('Error moving delivery to served vehicle:', error)
      }
    },
  },
}
</script>

<style lang="scss">
.route-optimization-detail {
  height: 100%;

  &-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__fo-list {
    width: 100%;
    display: flex;
    flex-direction: row-reverse;

    &-btn {
      padding: 0;
      border-color: transparent;
      background-color: transparent;

      &:hover {
        border-color: transparent;
        background-color: transparent;
      }
    }
  }
}

.fo-alert {
  &__redirect-text {
    cursor: pointer;
    color: $mep-primary;
    flex-grow: 1;

    p {
      margin: 0;
    }
  }
}
</style>
